import { useCallback, useRef } from 'react';

/**
 * Parameters for the `useInfiniteScroll` hook.
 *
 * @typedef {Object} useInfiniteScrollParams
 * @property {boolean} isLoading - Indicates whether a loading request is in progress.
 * @property {boolean} hasMoreData - Indicates if there is more data to load.
 * @property {() => void} fetchNextChunkOfDataCallbackFn - Callback to retrieve more data.
 */
interface useInfiniteScrollParams {
  isLoading: boolean;
  hasMoreData: boolean;
  fetchNextChunkOfDataCallbackFn: () => void;
}

/**
 * A custom hook that sets up an IntersectionObserver to implement infinite scrolling.
 * Observes the last list item and loads more data when it becomes visible.
 *
 * @param {useInfiniteScrollParams} params - The configuration object for infinite scroll.
 * @returns {{
 *   intersectionObserver: React.MutableRefObject<IntersectionObserver>,
 *   lastElementCallbackRef: (element: Element) => void
 * }} An object with:
 *   - `intersectionObserver`: a mutable ref to the IntersectionObserver instance.
 *   - `lastElementCallbackRef`: a callback ref to attach to the last item in a scrollable list.
 */
export function useInfiniteScroll({
  isLoading,
  hasMoreData,
  fetchNextChunkOfDataCallbackFn,
}: useInfiniteScrollParams): {
  intersectionObserver: React.MutableRefObject<IntersectionObserver>;
  lastElementCallbackRef: (element: Element) => void;
} {
  const intersectionObserver = useRef<IntersectionObserver>();

  const lastElementCallbackRef = useCallback(
    (element: Element) => {
      if (isLoading) return;

      if (intersectionObserver.current)
        intersectionObserver.current.disconnect();

      intersectionObserver.current = new IntersectionObserver((elements) => {
        if (elements[0].isIntersecting && hasMoreData) {
          fetchNextChunkOfDataCallbackFn();
        }
      });

      if (element) intersectionObserver.current.observe(element);
    },
    [isLoading, hasMoreData],
  );

  return {
    intersectionObserver,
    lastElementCallbackRef,
  };
}
